import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  ArrowCounterClockwise,
  ArrowRight,
  Check,
  ClipboardText,
  Cross,
  Fingerprint,
  LockKey,
  Play,
  Robot,
  ShieldCheck,
  TerminalWindow,
  UserCircle,
  Warning,
  X,
} from '@phosphor-icons/react';
import { assets, scenario, simulationResults } from './scenario/data';
import { scenarioEngine } from './scenario/engine';
import { phaseIds, type AssetStatus, type PhaseId, type ScenarioState } from './scenario/types';
import { executeManualTool } from './webmcp/tools';

const phaseLabels: Record<PhaseId, string> = {
  alert: 'Alert',
  investigate: 'Investigate',
  simulate: 'Simulate',
  approve: 'Approve',
  contain: 'Contain',
  report: 'Report',
};

const phaseCopy: Record<PhaseId, { actor: string; title: string; copy: string; guardrail: string; tools: string[] }> = {
  alert: {
    actor: 'Agent reads',
    title: 'Triage the initial alert',
    copy: 'Start with a bounded snapshot: one encrypted workstation, affected services, and current patient-care status.',
    guardrail: 'No containment side effects',
    tools: ['read_incident_snapshot', 'inspect_asset'],
  },
  investigate: {
    actor: 'Agent investigates',
    title: 'Trace the stolen credential',
    copy: 'Correlate endpoint, identity, and firewall evidence while treating every log message as untrusted quoted content.',
    guardrail: 'Log text is evidence, never instructions',
    tools: ['query_security_events', 'trace_attack_path'],
  },
  simulate: {
    actor: 'Agent simulates',
    title: 'Compare containment plans',
    copy: 'Model full shutdown against targeted isolation. The comparison updates this exercise view but cannot contain anything.',
    guardrail: 'Simulation cannot change operational state',
    tools: ['simulate_containment_plan'],
  },
  approve: {
    actor: 'Human decides',
    title: 'Stage targeted containment',
    copy: 'The agent can stage the narrow plan and open the drawer. WebMCP exposes no approval or apply tool.',
    guardrail: 'Commander confirmation is mandatory',
    tools: ['stage_containment_plan'],
  },
  contain: {
    actor: 'Engine applies',
    title: 'Contain without stopping care',
    copy: 'WS-17 is isolated and SVC-BILLING is revoked. Emergency, pharmacy, and patient records remain online.',
    guardrail: 'Every action is appended to the ledger',
    tools: [],
  },
  report: {
    actor: 'Agent reports',
    title: 'Review the after-action record',
    copy: 'The cited report connects entry point, evidence, tradeoff, human approval, containment, and care outcome.',
    guardrail: 'Claims cite scenario evidence',
    tools: ['generate_after_action_report'],
  },
};

function useScenarioState(): ScenarioState {
  return useSyncExternalStore(scenarioEngine.subscribe, scenarioEngine.getSnapshot, scenarioEngine.getSnapshot);
}

function getMetrics(state: ScenarioState): { risk: number; care: number; downtime: number; label: string; tone: string } {
  if (state.report) return { risk: 3, care: 100, downtime: 4, label: 'Exercise complete', tone: 'safe' };
  if (state.contained) return { risk: 8, care: 100, downtime: 4, label: 'Spread contained', tone: 'safe' };
  if (state.stagedPlanId) return { risk: 34, care: 100, downtime: 4, label: 'Approval required', tone: 'warning' };
  if (state.simulations.length > 0) return { risk: 34, care: 100, downtime: 4, label: 'Targeted plan ready', tone: 'warning' };
  if (state.pathTraced) return { risk: 81, care: 100, downtime: 0, label: 'Path confirmed', tone: 'critical' };
  return { risk: 72, care: 100, downtime: 0, label: 'Elevated spread risk', tone: 'critical' };
}

function statusForAsset(id: string, state: ScenarioState): AssetStatus {
  if (state.contained) return 'safe';
  if (id === 'WS-17') return 'critical';
  if ((id === 'SVC-BILLING' || id === 'CLINICAL-FS-01') && state.pathTraced) return 'warning';
  if (id === 'ED-CARE' || id === 'EHR-CORE' || id === 'PHARM-01') return 'safe';
  return 'neutral';
}

function MapNode({ id, x, y, title, subtitle, state }: { id: string; x: number; y: number; title: string; subtitle: string; state: ScenarioState }) {
  const status = statusForAsset(id, state);
  return (
    <g className={`map-node is-${status}`} transform={`translate(${x} ${y})`}>
      <rect width="150" height="58" rx="9" />
      <text x="14" y="24">{title}</text>
      <text className="map-node-sub" x="14" y="43">{subtitle}</text>
    </g>
  );
}

function DependencyMap({ state }: { state: ScenarioState }) {
  const metrics = getMetrics(state);
  const pathClass = state.pathTraced ? 'is-path' : '';
  const blockedClass = state.contained ? 'is-blocked' : pathClass;
  return (
    <section className="map-area" aria-labelledby="map-title">
      <div className="map-head">
        <div>
          <div className="section-label">Live dependency map</div>
          <h2 id="map-title">Hospital services and attack path</h2>
        </div>
        <div className={`live-state is-${metrics.tone}`}><span aria-hidden="true" />{metrics.label}</div>
      </div>
      <div id="care-status" className="metric-row" aria-live="polite">
        <div className="metric"><span>Spread risk</span><strong>{metrics.risk}%</strong></div>
        <div className="metric"><span>Care online</span><strong>{metrics.care}%</strong></div>
        <div className="metric"><span>Downtime</span><strong>{metrics.downtime} min</strong></div>
      </div>
      <svg className="network" viewBox="0 0 620 330" role="img" aria-labelledby="network-title network-desc">
        <title id="network-title">Hospital service dependency map</title>
        <desc id="network-desc">A compromised billing workstation connects through a stolen service identity toward a clinical file server. Emergency, pharmacy, and patient records remain online.</desc>
        <line className={`map-edge ${blockedClass}`} x1="160" y1="240" x2="290" y2="175" />
        <line className={`map-edge ${blockedClass}`} x1="365" y1="175" x2="470" y2="165" />
        <line className="map-edge" x1="485" y1="145" x2="490" y2="75" />
        <line className="map-edge" x1="460" y1="168" x2="285" y2="70" />
        <line className="map-edge" x1="505" y1="185" x2="525" y2="260" />
        <MapNode id="WS-17" x={55} y={222} title="Billing WS-17" subtitle={state.contained ? 'Isolated' : 'Encrypted · beacon blocked'} state={state} />
        <MapNode id="SVC-BILLING" x={245} y={146} title="SVC-BILLING" subtitle={state.contained ? 'Revoked' : 'Service identity'} state={state} />
        <MapNode id="CLINICAL-FS-01" x={420} y={138} title="Clinical files" subtitle={state.contained ? 'Protected' : 'Access blocked'} state={state} />
        <MapNode id="EHR-CORE" x={420} y={28} title="Patient records" subtitle="Operational" state={state} />
        <MapNode id="ED-CARE" x={210} y={28} title="Emergency dept." subtitle="Critical care online" state={state} />
        <MapNode id="PHARM-01" x={420} y={242} title="Pharmacy" subtitle="Dispensing online" state={state} />
      </svg>
      <div className="mobile-network" aria-label="Mobile hospital service dependency summary">
        <div className="mobile-path">
          <div className={`mobile-node is-${statusForAsset('WS-17', state)}`}><strong>Billing WS-17</strong><span>{state.contained ? 'Isolated' : 'Encrypted · beacon blocked'}</span></div>
          <span className="mobile-arrow" aria-hidden="true">↓</span>
          <div className={`mobile-node is-${statusForAsset('SVC-BILLING', state)}`}><strong>SVC-BILLING</strong><span>{state.contained ? 'Revoked' : 'Credential replay'}</span></div>
          <span className="mobile-arrow" aria-hidden="true">↓</span>
          <div className={`mobile-node is-${statusForAsset('CLINICAL-FS-01', state)}`}><strong>Clinical files</strong><span>{state.contained ? 'Protected' : 'Access blocked'}</span></div>
        </div>
        <div className="mobile-care">
          <span>Emergency online</span><span>Records online</span><span>Pharmacy online</span>
        </div>
      </div>
    </section>
  );
}

function PhaseNav({ state, viewedPhase, onView }: { state: ScenarioState; viewedPhase: PhaseId; onView: (phase: PhaseId) => void }) {
  return (
    <nav className="phase-nav" aria-label="Exercise phases">
      <ol>
        {phaseIds.map((phase, index) => {
          const reached = index <= state.reachedPhase;
          const current = phase === state.phase;
          return (
            <li key={phase}>
              <button
                type="button"
                className={viewedPhase === phase ? 'is-selected' : ''}
                disabled={!reached}
                aria-current={current ? 'step' : undefined}
                aria-pressed={viewedPhase === phase}
                onClick={() => onView(phase)}
              >
                <span className="phase-index">{index + 1}</span>
                <span>{phaseLabels[phase]}</span>
                {index < state.reachedPhase ? <Check className="phase-check" weight="bold" aria-label="complete" /> : null}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function SimulationComparison({ state }: { state: ScenarioState }) {
  if (state.simulations.length === 0) return null;
  return (
    <div className="simulation-comparison" aria-label="Containment plan comparison">
      {(['full_shutdown', 'targeted_isolation'] as const).map((planId) => {
        const result = simulationResults[planId];
        return (
          <article className={result.recommended ? 'is-recommended' : ''} key={planId}>
            <div className="comparison-head">
              <strong>{result.label}</strong>
              <span>{result.recommended ? 'Preferred' : 'Reject'}</span>
            </div>
            <div className="comparison-metrics">
              <span><b>{result.spreadRisk}%</b> spread</span>
              <span><b>{result.careOnline}%</b> care</span>
              <span><b>{result.downtimeMinutes}m</b> downtime</span>
            </div>
            <p>{result.verdict}</p>
          </article>
        );
      })}
    </div>
  );
}

function ReportPreview({ state }: { state: ScenarioState }) {
  if (!state.report) return null;
  return (
    <div className="report-preview">
      <div className="report-title"><ClipboardText size={18} /><strong>Cited after-action report</strong></div>
      <p>Entry point: <strong>Billing WS-17</strong> <a href="#evidence-EVT-101">[EVT-101]</a></p>
      <p>Containment: <strong>isolate endpoint + revoke identity</strong> <a href="#ledger">[LEDGER]</a></p>
      <p>Care outcome: <strong>all critical services online</strong> <a href="#care-status">[CARE-201-203]</a></p>
    </div>
  );
}

async function runManualPhase(phase: PhaseId): Promise<void> {
  if (phase === 'alert') {
    await executeManualTool(scenarioEngine, 'read_incident_snapshot');
    await executeManualTool(scenarioEngine, 'inspect_asset', { asset_id: 'WS-17' });
  } else if (phase === 'investigate') {
    await executeManualTool(scenarioEngine, 'inspect_asset', { asset_id: 'SVC-BILLING' });
    await executeManualTool(scenarioEngine, 'query_security_events', { limit: 5 });
    await executeManualTool(scenarioEngine, 'trace_attack_path', { from_asset_id: 'WS-17', to_asset_id: 'CLINICAL-FS-01' });
  } else if (phase === 'simulate') {
    await executeManualTool(scenarioEngine, 'simulate_containment_plan', { plan_ids: ['full_shutdown', 'targeted_isolation'] });
  } else if (phase === 'approve') {
    if (scenarioEngine.getSnapshot().stagedPlanId) scenarioEngine.openApprovalDrawer();
    else await executeManualTool(scenarioEngine, 'stage_containment_plan', { plan_id: 'targeted_isolation' });
  } else if (phase === 'contain') {
    scenarioEngine.continueToReport();
  } else {
    if (scenarioEngine.getSnapshot().report) scenarioEngine.reset();
    else await executeManualTool(scenarioEngine, 'generate_after_action_report', { format: 'markdown' });
  }
}

function actionLabel(phase: PhaseId, state: ScenarioState): string {
  if (phase === 'alert') return 'Read bounded snapshot';
  if (phase === 'investigate') return 'Correlate the evidence';
  if (phase === 'simulate') return 'Compare both plans';
  if (phase === 'approve') return state.stagedPlanId ? 'Review staged plan' : 'Stage targeted plan';
  if (phase === 'contain') return 'Continue to report';
  return state.report ? 'Replay scenario' : 'Generate cited report';
}

function CommandPanel({ state, viewedPhase, onReturnToCurrent }: { state: ScenarioState; viewedPhase: PhaseId; onReturnToCurrent: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const copy = phaseCopy[viewedPhase];
  const isPastView = viewedPhase !== state.phase;
  const stepIndex = phaseIds.indexOf(viewedPhase) + 1;

  const handleAction = async () => {
    if (isPastView) {
      onReturnToCurrent();
      return;
    }
    setBusy(true);
    setError('');
    try {
      await runManualPhase(viewedPhase);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The manual demo step failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="command-area" aria-labelledby="command-title">
      <div className="command-meta">
        <span className="section-label">Step {stepIndex} of 6</span>
        <span className={copy.actor.startsWith('Human') ? 'actor human' : 'actor'}>
          {copy.actor.startsWith('Human') ? <UserCircle size={15} /> : <Robot size={15} />}{copy.actor}
        </span>
      </div>
      <h2 id="command-title">{copy.title}</h2>
      <p className="command-copy">{copy.copy}</p>
      {copy.tools.length > 0 ? (
        <ul className="tool-list" aria-label="WebMCP tools for this phase">
          {copy.tools.map((tool) => (
            <li key={tool} className={state.invokedTools.includes(tool) ? 'is-called' : ''}>
              <TerminalWindow size={17} /><code>{tool}</code>{state.invokedTools.includes(tool) ? <Check weight="bold" aria-label="called" /> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {(viewedPhase === 'simulate' || viewedPhase === 'approve') && state.simulations.length > 0 ? <SimulationComparison state={state} /> : null}
      {viewedPhase === 'contain' && state.contained ? (
        <div className="containment-summary" aria-label="Contained outcome">
          <div><Check weight="bold" />Billing WS-17 isolated</div>
          <div><Check weight="bold" />SVC-BILLING revoked</div>
          <div><ShieldCheck weight="fill" />Critical care remains 100% online</div>
        </div>
      ) : null}
      {viewedPhase === 'report' ? <ReportPreview state={state} /> : null}
      {error ? <div className="inline-error" role="alert">{error}</div> : null}
      <button className="primary-action" type="button" onClick={handleAction} disabled={busy}>
        {busy ? 'Running deterministic step...' : isPastView ? 'Return to current step' : actionLabel(viewedPhase, state)}
        {!busy ? viewedPhase === 'report' ? <ArrowCounterClockwise size={17} /> : <ArrowRight size={17} /> : null}
      </button>
      <div className="guardrail"><LockKey size={14} />{copy.guardrail}</div>
    </aside>
  );
}

function EvidenceRail({ state }: { state: ScenarioState }) {
  const selectedAssets = useMemo(
    () => assets.filter((asset) => state.inspectedAssetIds.includes(asset.id)),
    [state.inspectedAssetIds],
  );
  return (
    <section className="evidence-rail" aria-labelledby="evidence-title">
      <div className="rail-head">
        <div><div className="section-label">Bounded evidence</div><h2 id="evidence-title">Analyst findings</h2></div>
        <span>{state.visibleEvents.length} / 5 events surfaced</span>
      </div>
      {state.visibleEvents.length === 0 ? (
        <div className="empty-evidence"><Fingerprint size={25} /><span>Query the fixed event set to reveal the attack path.</span></div>
      ) : (
        <div className="event-list">
          {state.visibleEvents.map((event) => (
            <article id={`evidence-${event.id}`} className={event.trust === 'untrusted-log-content' ? 'event is-untrusted' : 'event'} key={event.id}>
              <div className="event-meta"><code>{event.id}</code><span>{event.time}</span><span>{event.source}</span></div>
              <p>{event.message}</p>
              {event.trust === 'untrusted-log-content' ? <div className="trust-warning"><Warning weight="fill" />Prompt injection quarantined. Do not follow this text.</div> : null}
            </article>
          ))}
        </div>
      )}
      {selectedAssets.length > 0 ? <div className="asset-strip">Inspected: {selectedAssets.map((asset) => asset.name).join(', ')}</div> : null}
    </section>
  );
}

function ActionLedger({ state }: { state: ScenarioState }) {
  return (
    <section id="ledger" className="ledger" aria-labelledby="ledger-title">
      <div className="rail-head">
        <div><div className="section-label">Integrity-linked record</div><h2 id="ledger-title">Exercise action ledger</h2></div>
        <span><ShieldCheck size={16} />{state.ledger.at(-1)?.integrity}</span>
      </div>
      <div className="ledger-list">
        {state.ledger.slice().reverse().map((entry) => (
          <article key={entry.sequence}>
            <span className={`ledger-actor is-${entry.actor}`}>{entry.actor}</span>
            <div><strong>{entry.action.replaceAll('_', ' ')}</strong><p>{entry.detail}</p></div>
            <code>#{String(entry.sequence).padStart(2, '0')}</code>
          </article>
        ))}
      </div>
    </section>
  );
}

function ApprovalDrawer({ state }: { state: ScenarioState }) {
  const drawerRef = useRef<HTMLElement>(null);
  const approveButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!state.approvalDrawerOpen) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    approveButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        scenarioEngine.closeApprovalDrawer();
        return;
      }
      if (event.key !== 'Tab' || !drawerRef.current) return;
      const controls = Array.from(drawerRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'));
      const first = controls[0];
      const last = controls.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [state.approvalDrawerOpen]);

  if (!state.approvalDrawerOpen) return null;
  const plan = simulationResults.targeted_isolation;
  return (
    <div className="drawer-layer" role="presentation">
      <button className="drawer-scrim" aria-label="Close approval drawer" type="button" onClick={() => scenarioEngine.closeApprovalDrawer()} />
      <aside ref={drawerRef} className="approval-drawer" role="dialog" aria-modal="true" aria-labelledby="approval-title">
        <div className="drawer-head">
          <div className="drawer-icon"><UserCircle size={21} weight="fill" /></div>
          <button type="button" aria-label="Close approval drawer" onClick={() => scenarioEngine.closeApprovalDrawer()}><X size={20} /></button>
        </div>
        <div className="section-label">Commander approval</div>
        <h2 id="approval-title">Authorize targeted containment?</h2>
        <p>The browser agent staged this plan. No approval or apply capability is exposed through WebMCP.</p>
        <div className="approval-summary">
          {plan.actions.map((action) => <div key={action}><Check weight="bold" />{action}</div>)}
        </div>
        <div className="care-preserved">
          <ShieldCheck size={22} weight="fill" />
          <div><strong>Patient care preserved</strong><span>Emergency, pharmacy, and patient records stay online.</span></div>
        </div>
        <div className="human-boundary"><LockKey size={17} /><span>This control is intentionally absent from the WebMCP tool surface.</span></div>
        <button ref={approveButtonRef} className="approve-button" type="button" onClick={() => scenarioEngine.humanApproveAndContain()}>
          Approve and contain <ArrowRight size={18} />
        </button>
        <button className="cancel-button" type="button" onClick={() => scenarioEngine.closeApprovalDrawer()}>Keep plan staged</button>
      </aside>
    </div>
  );
}

export default function App() {
  const state = useScenarioState();
  const [viewedPhase, setViewedPhase] = useState<PhaseId>(state.phase);

  useEffect(() => setViewedPhase(state.phase), [state.phase]);

  const webMcpLabel = state.webMcpStatus === 'registered' ? 'WebMCP live' : 'Manual demo mode';
  return (
    <main className="page-shell">
      <section className="app-window" aria-label="Incident Command Rehearsal" inert={state.approvalDrawerOpen ? true : undefined}>
        <header className="topbar">
          <div className="brand">
            <div className="brand-mark"><Cross size={18} weight="bold" /></div>
            <div><h1>Incident Command Rehearsal</h1><span>Northstar Hospital · Scenario 01</span></div>
          </div>
          <div className={`webmcp-state is-${state.webMcpStatus}`} title={state.webMcpDetail} role="status" aria-live="polite">
            <span aria-hidden="true" />{webMcpLabel}
          </div>
        </header>
        <PhaseNav state={state} viewedPhase={viewedPhase} onView={setViewedPhase} />
        <div className="prompt-bar">
          <Robot size={19} weight="fill" />
          <span>Core demo prompt</span>
          <strong>“{scenario.prompt}”</strong>
        </div>
        <div className="content-grid">
          <DependencyMap state={state} />
          <CommandPanel state={state} viewedPhase={viewedPhase} onReturnToCurrent={() => setViewedPhase(state.phase)} />
        </div>
        <div className="lower-grid">
          <EvidenceRail state={state} />
          <ActionLedger state={state} />
        </div>
        <footer className="safety-footer">
          <span><ShieldCheck size={16} weight="fill" />Safe deterministic simulation</span>
          <span>No real attack actions</span>
          <span>No real hospital or patient data</span>
          <span>Scenario ID {scenario.id}</span>
        </footer>
      </section>
      <ApprovalDrawer state={state} />
    </main>
  );
}
